import { groupBy } from "lodash";
import { useMemo } from "react";

import {
  Chip,
  ListSubheader,
  Stepper,
  listSubheaderClasses,
  stepConnectorClasses,
  stepContentClasses,
  styled,
} from "@mui/material";

import TimelineItem from "./TimelineItem";
import TimelinePopover from "./TimelinePopover";
import { Datapoint, SeriesType } from "../hooks/useVisualisationData";
import { PatientData } from "../models/patient-data";

const StyledStepper = styled(Stepper)(({ theme }) => ({
  [`& .${stepConnectorClasses.line}, & .${stepContentClasses.root}`]: {
    borderColor: theme.palette.divider,
    borderWidth: "0.75px",
  },
  [theme.breakpoints.down("md")]: {
    [`& .${stepConnectorClasses.root}`]: {
      marginLeft: "50%",
    },
    [`& .${stepContentClasses.root}`]: {
      border: "none",
      marginLeft: theme.spacing(0),
      paddingLeft: theme.spacing(0),
      paddingRight: theme.spacing(0),
    },
  },
  [`& .${listSubheaderClasses.root} + .${stepConnectorClasses.root}`]: {
    display: "none",
  },
}));

export const Timeline: React.FC<{
  series: SeriesType[];
  dataset: Datapoint[];
  references?: Pick<PatientData["data"], "medicaments" | "weeklyTargets">;
}> = ({ dataset, series, references }) => {
  const items = useMemo(() => {
    const dataByYear = groupBy(dataset, (o) =>
      new Date(o.timestamp).getFullYear(),
    );

    return Object.keys(dataByYear)
      .sort((a, b) => Number(b) - Number(a))
      .map((year) => {
        return [
          <ListSubheader
            key={year}
            sx={{ background: "none", textAlign: "center", py: 1 }}
          >
            <Chip
              label={year}
              sx={{
                bgcolor: "background.paper",
                borderColor: "divider",
                borderWidth: "0.75px",
              }}
              variant="outlined"
            />
          </ListSubheader>,
          dataByYear[year].map((data) => (
            <TimelineItem
              datapoint={data}
              key={data.timestamp}
              references={references}
              series={series}
            />
          )),
        ];
      });
  }, [dataset, references, series]);

  if (!items) return null;

  return (
    <>
      <StyledStepper nonLinear orientation="vertical">
        {items}
      </StyledStepper>
      <TimelinePopover />
    </>
  );
};

export default Timeline;
